import React from 'react';
import { styled } from '@compiled/react';
import ChevronRightIcon from '@atlaskit/icon/core/migration/chevron-right';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';

import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages.tsx';
import { useSyncNewLozenge } from './sync-new-lozenge/index.tsx';

type CollapsibleAreaHeaderProps = {
	totalFields: number;
	isExpanded: boolean;
	onClick: (_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
};

export const HEADER_BUTTON_TEST_ID =
	'issue-create.ui.modal.create-form.layout-renderer.fields-container.collapsible-area-header.collapsible-area-header';

export const CollapsibleAreaHeader = ({
	totalFields,
	isExpanded,
	onClick,
}: CollapsibleAreaHeaderProps) => {
	const { formatMessage } = useIntl();

	const buttonAlt = isExpanded ? messages.hideMoreFields : messages.showMoreFields;

	const { isSmartFieldsNew } = useSyncNewLozenge();

	return (
		<Box xcss={buttonContainerStyles}>
			<HeaderButton
				iconBefore={
					isExpanded ? (
						<ChevronDownIcon color="currentColor" label="" size="small" />
					) : (
						<ChevronRightIcon color="currentColor" label="" size="small" />
					)
				}
				appearance="subtle-link"
				title={formatMessage(buttonAlt, { totalFields })}
				onClick={onClick}
				testId={HEADER_BUTTON_TEST_ID}
				shouldFitContainer
			>
				<HeaderButtonContent>
					{formatMessage(messages.moreFields, { totalFields })}
					{isSmartFieldsNew && (
						<>
							{' '}
							<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
						</>
					)}
				</HeaderButtonContent>
			</HeaderButton>
		</Box>
	);
};

const buttonHeight = '40px';

const buttonContainerStyles = xcss({
	// Button ring outline displays outside collapsible area. This padding is used to make them the same position
	padding: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderButton = styled(Button)({
	height: buttonHeight,
	textAlign: 'left',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderButtonContent = styled.span({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: buttonHeight,
	marginLeft: token('space.075'),
});
